import { useRecordInspectionStep } from '@/api/equipment/inspection-logs/recordInspectionStep';
import ButtonGroup from '@/components/Elements/ButtonGroup/ButtonGroup';
import { Button } from '@/components/ui/elements/button';
import { Textarea } from '@/components/ui/elements/textarea';
import {
  InspectionLog,
  InspectionStep as InspectionStepType,
  InspectionStepResult,
  ModelID,
} from '@/types';
import { InformationCircleIcon } from '@heroicons/react/24/outline';
import { useState } from 'react';
import { InspectionLogStepPhotoManager } from './InspectionLogStepPhotoManager';

interface InspectionStepProps {
  step: InspectionStepType & { result?: InspectionStepResult };
  inspectionLog: InspectionLog;
}

export const InspectionStep = ({ step, inspectionLog }: InspectionStepProps) => {
  const [notes, setNotes] = useState(step.result?.notes || '');
  const [showHowTo, setShowHowTo] = useState(false);
  const status = step.result?.status || undefined;
  const { mutate: logInspectionStepResult } = useRecordInspectionStep();

  const handleSaveNotes = () => {
    handleLogStepResult(step.inspection_step_id, status);
  };

  const handleUpdateStatus = (newStatus: 'pass' | 'warning' | 'fail') => {
    handleLogStepResult(step.inspection_step_id, newStatus);
  };

  const handleLogStepResult = (stepId: ModelID, status?: 'pass' | 'warning' | 'fail') => {
    if (!inspectionLog || !inspectionLog.inspection || !stepId) return;

    logInspectionStepResult(
      {
        inspection: inspectionLog.inspection,
        log: inspectionLog,
        inspection_step_id: stepId,
        data: {
          status,
          notes,
        },
      },
      {
        onSuccess: () => {
          // queryClient.invalidateQueries({ queryKey: vehicleFeatureKeys.all });
          // queryClient.invalidateQueries({ queryKey: assetFeatureKeys.all });
        },
      },
    );
  };

  return (
    <div key={step.id} className="mt-6 p-4 border rounded-md w-full">
      <div className="flex flex-col md:flex-row md:flex-wrap justify-between md:space-x-2 mb-2">
        <h2 className="text-xl font-semibold mb-2">{step.name}</h2>
        <ButtonGroup>
          <ButtonGroup.Button
            type="button"
            className={
              status === 'fail'
                ? 'bg-red-600 text-white hover:bg-red-700 focus:ring-red-500 w-full'
                : 'hover:bg-red-500 w-full disabled:bg-background/20 disabled:text-foreground/40'
            }
            onClick={() => handleUpdateStatus('fail')}
            disabled={inspectionLog?.is_complete}
          >
            Fail
          </ButtonGroup.Button>
          <ButtonGroup.Button
            type="button"
            className={
              status === 'warning'
                ? 'bg-yellow-500 text-white hover:bg-yellow-600 focus:ring-yellow-400 w-full'
                : 'hover:bg-yellow-500 w-full disabled:bg-background/20 disabled:text-foreground/40'
            }
            onClick={() => handleUpdateStatus('warning')}
            disabled={inspectionLog?.is_complete}
          >
            Warning
          </ButtonGroup.Button>

          <ButtonGroup.Button
            type="button"
            className={
              status === 'pass'
                ? 'bg-green-600 text-white hover:bg-green-700 focus:ring-green-500 w-full'
                : 'hover:bg-green-500 w-full disabled:bg-background/20 disabled:text-foreground/40'
            }
            onClick={() => handleUpdateStatus('pass')}
            disabled={inspectionLog?.is_complete}
          >
            Pass
          </ButtonGroup.Button>
        </ButtonGroup>
      </div>

      <>
        {inspectionLog?.is_complete ? (
          <p>{step.result?.notes}</p>
        ) : (
          <>
            <p className="mb-2 text-sm text-foreground/85">
              {step.description}{' '}
              <a
                onClick={() => setShowHowTo(!showHowTo)}
                className="text-blue-500 hover:underline hover:cursor-pointer"
              >
                how to <InformationCircleIcon className="inline-block w-4 h-4 mb-1" />
              </a>
            </p>
            {showHowTo && <p className="mb-2">{step.how_to}</p>}
            <div className="relative">
              <Textarea
                placeholder="Add your notes here..."
                defaultValue={notes}
                onBlur={(event) => {
                  if (event.target.value !== notes) {
                    setNotes(event.target.value);
                    handleSaveNotes();
                  }
                }}
              />
              {step.result?.notes !== notes && (
                <Button
                  type="button"
                  variant="outline"
                  className="mt-2 absolute right-2 bottom-2"
                  onClick={handleSaveNotes}
                >
                  Save Notes
                </Button>
              )}
            </div>
            {step.result?.is_note_required && status !== 'pass' && (
              <p className="text-sm text-red-600 p-1">* Note required.</p>
            )}
          </>
        )}
        <div className="mt-3 flex flex-col gap-1">
          <InspectionLogStepPhotoManager
            inspectionLog={inspectionLog}
            stepId={step.id}
            result={step.result}
          />
        </div>
        {!inspectionLog?.is_complete && step.is_photo_required && (
          <p className="text-sm text-red-600">* Photo required.</p>
        )}
      </>
    </div>
  );
};
